import { API } from './apiConstants';

export interface UploadedFileReference {
  uploadId: string;
  name: string;
  type: string;
  size: number;
  lastModified: number;
  expiresAt: string;
}

export class UploadRequestError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

export function uploadFile(
  file: File,
  configVersion: string,
  project: string,
  fieldName: string,
  onProgress: (progress: number) => void
): { promise: Promise<UploadedFileReference>; abort: () => void } {
  const request = new XMLHttpRequest();
  const promise = new Promise<UploadedFileReference>((resolve, reject) => {
    request.open(
      'POST',
      API.V3.UPLOADS.CREATE(configVersion, project, fieldName)
    );
    request.timeout = 120000;
    request.ontimeout = () =>
      reject(new UploadRequestError(408, 'Upload timed out'));
    request.upload.onprogress = (event) => {
      if (event.lengthComputable)
        onProgress(
          Math.min(99, Math.round((event.loaded / event.total) * 100))
        );
    };
    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        let code: string | undefined;
        try {
          code = JSON.parse(request.responseText)?.validationErrors?.[fieldName]
            ?.code;
        } catch (_) {}
        reject(new UploadRequestError(request.status, 'Upload failed', code));
        return;
      }
      try {
        const reference = JSON.parse(request.responseText);
        if (!reference.uploadId || !reference.expiresAt)
          throw new Error('Invalid upload response');
        resolve(reference);
      } catch (error) {
        reject(
          new UploadRequestError(request.status, 'Invalid upload response')
        );
      }
    };
    request.onerror = () => reject(new UploadRequestError(0, 'Network error'));
    request.onabort = () => reject(new UploadRequestError(0, 'Upload aborted'));
    const body = new FormData();
    body.append('file', file);
    body.append('lastModified', String(file.lastModified));
    request.send(body);
  });
  return { promise, abort: () => request.abort() };
}

export async function deleteUploadedFile(id: string): Promise<void> {
  const response = await fetch(API.V3.UPLOADS.DELETE(id), { method: 'DELETE' });
  if (!response.ok)
    throw new UploadRequestError(response.status, 'Delete failed');
}
